import React from 'react'
import { Box, DialogContent } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { HeatpumpIo } from 'src/Libs/flow/installation/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useDialogActionsPositioned } from '../../../hooks'
import Actions from '@e3dc-react/shell/Elements/Dialog/ActionDialog/Actions'

interface AddHeatpumpForm {
  name: string
}

interface AddHeatpumpProps {
  error: string | null
  closeDialog: () => void
  heatpump: HeatpumpIo | null
  addHeatpump: (name: string) => void
}

/**
 * Form to add a heatpump to the installation. Thought of as content inside a dialog.
 */
export const AddHeatpump: React.FC<AddHeatpumpProps> = ({ error, closeDialog, heatpump, addHeatpump }) => {
  const { t } = useCustomTranslation()

  const methods = useForm<AddHeatpumpForm>({
    defaultValues: { name: t('installation.heatpumpEditor.default-device-name') },
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const { handleSubmit, formState } = methods

  const onSubmit: SubmitHandler<AddHeatpumpForm> = data => {
    addHeatpump(data.name)
    closeDialog()
  }
  const isSaveButtonEnabled = !error && formState.isValid

  return (
    <FormProvider {...methods}>
      <Box component={'form'} onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100%' }}>
        <DialogContent dividers sx={{ px: 0, py: 4 }}>
          {error ? (
            <Box>
              <Alert severity="warning" sx={{ marginBottom: 2 }} text={error} />
              {!!heatpump && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <ShellTextField
                    sx={{ flex: 1 }}
                    label={t('common.name')}
                    value={getDeviceDefaultName(heatpump?.name || '', 'Heatpump', heatpump?.deviceId || '', t)}
                    disabled
                  />
                  <ShellTextField
                    sx={{ flex: 0.25 }}
                    label={t('productEditor.pairingEditor.pairingID.modbus-wallbox')}
                    value={heatpump?.pairingId}
                    disabled
                  />
                </Box>
              )}
            </Box>
          ) : (
            <TextField name="name" label={t('installation.productEditor.deviceName')} fullWidth />
          )}
        </DialogContent>
        <Actions
          actions={useDialogActionsPositioned(
            t('installation.productEditor.edit.heatpump'),
            handleSubmit(onSubmit),
            !isSaveButtonEnabled,
            closeDialog
          )}
        />
      </Box>
    </FormProvider>
  )
}

const validationSchema = z.object({
  name: z.string().nonempty(),
})
